import React, { useLayoutEffect, useRef, useMemo, useEffect } from 'react';
import { ArrowExpand01Icon, ArrowUp01Icon } from "hugeicons-react";
import { ChevronDown } from "lucide-react";
import "../../Styles/Layout/ResourcePreview.css";
import { AIResponseRenderer } from "../../handler/TextRenderer/LLMTextRenderer";
import CodeDisplay from "../Partials/CodeDisplay";
import VisualizationRenderer from '../visualizations/VisualizationRenderer';
import { Resource } from '../../types/resource';
import { getResourceIcon } from '../../Data/IconData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const VIZ_COLLAPSED_KEY_PREFIX = 'studybuddy_visualization_collapsed';

function getVisualizationCollapsedKey(messageId?: string, title?: string): string {
  const parts = [VIZ_COLLAPSED_KEY_PREFIX];
  const id = messageId?.trim();
  if (id) parts.push(id);
  const slug = (title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) parts.push(slug);
  return parts.join('__');
}

interface ResourcePreviewProps {
  resource: Resource;
  onExpand: () => void;
  isLoading?: boolean;
  compact?: boolean;
}

const ResourcePreviewComponent = ({ resource, onExpand, isLoading = false, compact = false }: ResourcePreviewProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isViz = resource.type === "visualization";
    const reduceMotion = useReducedMotion();

    const vizCollapsedKey = getVisualizationCollapsedKey(resource.messageId, resource.title);
    const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>(
        vizCollapsedKey,
        false,
        { enabled: isViz }
    );

    useLayoutEffect(() => {
        if (isLoading && contentRef.current) {
            requestAnimationFrame(() => {
                if (contentRef.current) {
                    (contentRef.current as HTMLElement).scrollTop = (contentRef.current as HTMLElement).scrollHeight;
                }
            });
        }
    }, [isLoading, resource.content]);

    useEffect(() => {
        if (isLoading) {
            scrollIntervalRef.current = setInterval(() => {
                if (contentRef.current) {
                    contentRef.current.scrollTop = contentRef.current.scrollHeight;
                }
            }, 100);
        } else {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }
        }
        return () => {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
            }
        };
    }, [isLoading]);

    const renderResourceContent = useMemo(() => {
        const resourceType = resource.type;

        if (resourceType === "essay") {
            return (
                <AIResponseRenderer
                    content={stripMessageContent(resource.content)}
                    isGenerating={isLoading}
                />
            );
        }
        if (resourceType === "code") {
            const language = resource.language || 'plaintext';
            return (
                <CodeDisplay
                    language={language}
                    content={resource.content}
                    showHeader={false}
                />
            );
        }
        if (resourceType === "visualization") {
            return (
                <VisualizationRenderer
                    content={resource.content}
                    isLoading={isLoading}
                    collapsed={isCollapsed}
                />
            );
        }
        return null;
    }, [resource.type, resource.content, resource.language, isLoading, isCollapsed]);

    return (
        <motion.div
            layout="size"
            className={`resource-preview-transition${compact ? ' is-compact' : ''}`}
            transition={{
                layout: reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.42, ease: [0.23, 1, 0.32, 1] },
            }}
        >
            <AnimatePresence initial={false} mode="popLayout">
                {compact ? (
                    <motion.button
                        key="compact"
                        type="button"
                        className="resource-preview-compact"
                        onClick={onExpand}
                        aria-label={`Open ${resource.title}`}
                        initial={reduceMotion ? false : { opacity: 0, y: -5, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 4, scale: 0.985 }}
                        transition={{ duration: reduceMotion ? 0 : 0.22, ease: 'easeOut' }}
                    >
                        <span className="resource-preview-header-left">
                            {getResourceIcon(resource.type)}
                            <span className="resource-preview-compact-title">{resource.title}</span>
                        </span>
                        <ArrowExpand01Icon size={15} aria-hidden />
                    </motion.button>
                ) : isViz ? (
                    <motion.div
                        key="visualization"
                        className="resource-preview-visualization-shell"
                        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -5 }}
                        transition={{ duration: reduceMotion ? 0 : 0.24, ease: 'easeOut' }}
                    >
                        {renderResourceContent}

                        <div className="resource-preview-visualization-action">
                            {isLoading ? (
                                <div className="resource-loading-spinner" />
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setIsCollapsed(prev => !prev)}
                                        aria-label={isCollapsed ? 'Show visualization' : 'Hide visualization'}
                                    >
                                        {isCollapsed ? <ChevronDown size={15} aria-hidden /> : <ArrowUp01Icon size={15} />}
                                    </button>
                                    <button type="button" onClick={onExpand} aria-label="Expand visualization">
                                        <ArrowExpand01Icon size={15} />
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="full"
                        className="resource-preview"
                        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -5 }}
                        transition={{ duration: reduceMotion ? 0 : 0.24, ease: 'easeOut' }}
                    >
                        <div className="resource-preview-header">
                            <div className="resource-preview-header-left">
                                {getResourceIcon(resource.type)}
                                <h1>{resource.title || (isLoading ? 'Generating Resource...' : '')}</h1>
                            </div>
                            <div className="resource-preview-header-right">
                                {isLoading ? (
                                    <div className="resource-loading-spinner" />
                                ) : (
                                    <button type="button" onClick={onExpand} aria-label="Expand">
                                        <ArrowExpand01Icon size={15} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="resource-preview-main">
                            <div
                                ref={contentRef}
                                className={`resource-preview-content ${isLoading ? 'loading' : ''} ${resource.type === 'code' ? 'code' : ''}`}
                            >
                                {renderResourceContent}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const stripMessageContent = (content: string) => {
    return content
        .replace(/<artifacts>[\s\S]*?<\/artifacts>/g, '')
        .replace(/<resource>[\s\S]*?<\/resource>/g, '')
        .replace(/\[Generation stopped\]/g, '')
        .trim()
        .split('<|endofinput|>')[0];
};

const areEqual = (prevProps: ResourcePreviewProps, nextProps: ResourcePreviewProps) => {
    const prevResource = prevProps.resource;
    const nextResource = nextProps.resource;
    return (
        prevResource.id === nextResource.id &&
        prevResource.title === nextResource.title &&
        prevResource.content === nextResource.content &&
        prevResource.type === nextResource.type &&
        prevProps.onExpand === nextProps.onExpand &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.compact === nextProps.compact
    );
};

export const ResourcePreview = React.memo(ResourcePreviewComponent, areEqual);
